import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { systemLogin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getFirstAvailableRoute } from '../utils/routeUtils';

const SystemLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    username: '',
    password: '',
  });
  const navigate = useNavigate();
  const { login, isAuthenticated, isSystemAdmin, isTenantAdmin, hasPermission } = useAuth();

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      // System login is always for system admin, so redirect to tenants
      navigate('/dashboard/tenants', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const validateUsername = (username: string): string => {
    if (!username) return 'Username is required';
    if (username.length < 3) return 'Username must be at least 3 characters';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // CRITICAL: Always prevent default form submission to avoid page refresh
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent multiple submissions
    if (loading) {
      return false;
    }
    
    setError('');

    // Validate inputs
    const usernameError = validateUsername(username);
    const passwordError = validatePassword(password);

    setValidationErrors({
      username: usernameError,
      password: passwordError,
    });

    if (usernameError || passwordError) {
      if (usernameError) setError(usernameError);
      else if (passwordError) setError(passwordError);
      return false; // Prevent form submission
    }

    setLoading(true);

    try {
      const response = await systemLogin(username, password);
      const { token, user } = response.data.data;
      
      login(token, user);
      
      // System login is always for system admin, so redirect to tenants
      navigate('/dashboard/tenants');
    } catch (err: any) {
      // Provide clearer error messages with safe property access
      let errorMessage = 'Login failed. Please try again.';
      
      try {
        if (err && typeof err === 'object') {
          if (err.response && err.response.data && err.response.data.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = err.message;
          }
        } else if (typeof err === 'string') {
          errorMessage = err;
        }
      } catch (parseError) {
        // If error parsing fails, use default message
        console.error('Error parsing login error:', parseError);
      }
      
      // Transform error message to be more user-friendly
      const lowerMessage = errorMessage.toLowerCase();
      if (lowerMessage.includes('invalid credentials') || 
          lowerMessage.includes('unauthorized') ||
          lowerMessage.includes('401')) {
        setError('Invalid username or password. Please check your credentials and try again.');
      } else if (lowerMessage.includes('network') || lowerMessage.includes('timeout')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
    
    return false; // Always return false to prevent form submission
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: '450px' }}>
        <div className="login-header" style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '42px',
            margin: '0 auto 24px',
            boxShadow: '0 10px 25px rgba(102, 126, 234, 0.3)'
          }}>
            🔐
          </div>
          <h1 style={{ fontSize: '30px', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
            System Admin
          </h1>
          <p style={{ fontSize: '17px', color: '#666' }}>
            Enter your credentials to access the admin panel
          </p>
        </div>

        {error && (
          <div className="error-message" style={{
            background: '#fee',
            border: '2px solid #e74c3c',
            color: '#c33',
            padding: '14px 18px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '17px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            boxShadow: '0 2px 8px rgba(231, 76, 60, 0.2)'
          }}>
            <span style={{ fontSize: '22px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form 
          onSubmit={handleSubmit} 
          noValidate 
          action="#" 
          method="post"
          onKeyDown={(e) => {
            // Prevent form submission on Enter key if there are errors
            if (e.key === 'Enter' && (loading || error)) {
              e.preventDefault();
            }
          }}
        >
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="username" style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              fontSize: '16px',
              color: '#333'
            }}>
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setValidationErrors({ ...validationErrors, username: '' });
              }}
              placeholder="Enter your username"
              required
              autoFocus
              className={validationErrors.username ? 'input-error' : ''}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '17px',
                border: `2px solid ${validationErrors.username ? '#e74c3c' : '#e0e0e0'}`,
                borderRadius: '8px',
                transition: 'all 0.2s',
                outline: 'none',
                backgroundColor: validationErrors.username ? '#fff5f5' : 'white',
                color: '#1e293b'
              }}
              onFocus={(e) => e.target.style.borderColor = validationErrors.username ? '#e74c3c' : '#667eea'}
              onBlur={(e) => e.target.style.borderColor = validationErrors.username ? '#e74c3c' : '#e0e0e0'}
            />
            {validationErrors.username && (
              <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                {validationErrors.username}
              </small>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: '24px' }}>
            <label htmlFor="password" style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              fontSize: '16px',
              color: '#333'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setValidationErrors({ ...validationErrors, password: '' });
                }}
                placeholder="Enter your password"
                required
                className={validationErrors.password ? 'input-error' : ''}
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  fontSize: '17px',
                  border: `2px solid ${validationErrors.password ? '#e74c3c' : '#e0e0e0'}`,
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: validationErrors.password ? '#fff5f5' : 'white',
                  color: '#1e293b'
                }}
                onFocus={(e) => e.target.style.borderColor = validationErrors.password ? '#e74c3c' : '#667eea'}
                onBlur={(e) => e.target.style.borderColor = validationErrors.password ? '#e74c3c' : '#e0e0e0'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '22px',
                  padding: '4px',
                  color: '#999'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {validationErrors.password && (
              <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '6px', display: 'block' }}>
                {validationErrors.password}
              </small>
            )}
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading}
            onClick={(e) => {
              // Additional safety: prevent default button behavior
              if (loading) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '18px',
              fontWeight: '600',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            {loading ? 'Logging in...' : 'Login to Dashboard'}
          </button>
        </form>

        <div className="login-link" style={{ 
          textAlign: 'center', 
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <Link to="/login" style={{ 
            color: '#667eea', 
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Not a system admin? Login here →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SystemLogin;

